"use client";

import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';

import SaleBanner from "@/app/Components/sale-message";
import ProductCard from "../Components/produit/product_card";
import type { Product as ProductCardType } from "../Components/produit/product_card";
import Header from "@/app/Components/header";
import { API_BASE_URL } from '@/app/lib/api';

// --- Types ---
type Product = {
  id: string | number;
  slug?: string;
  name?: string;
  image?: string;
  images?: string[];
  price?: number | string;
  promoPrice?: number | string;
  oldPrice?: number | string;
  stock?: number | string;
  sku?: string;
  categoryId?: string | number | null;
  isOnSale?: boolean | string;
};

type Gamme = {
  id: string;
  nom: string;
  slug?: string;
  images?: string[];
};

type GammeDetail = {
  gamme: Gamme;
  products: Product[];
  categories: Array<{ id: string | number; name?: string; nom?: string }>;
};

// --- Composant Loading Skeleton (Chargement moderne) ---
const SkeletonLoader = () => (
  <div className="animate-pulse space-y-12">
    {/* Top Gammes Skeleton */}
    <div className="flex gap-4 overflow-hidden">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="w-40 h-40 bg-gray-200 rounded-2xl flex-shrink-0" />
      ))}
    </div>
    {/* Section Skeleton */}
    <div className="flex flex-col lg:flex-row gap-6 h-[400px]">
      <div className="w-full lg:w-1/4 bg-gray-200 rounded-2xl" />
      <div className="w-full lg:w-3/4 bg-gray-100 rounded-2xl" />
    </div>
  </div>
);

// --- Composant ProductSection ---
const ProductSection = ({ detail }: { detail: GammeDetail }) => {
  const { gamme, products, categories } = detail;
  const swiperRef = useRef<any>(null);

  // Mapping categories
  const categoryMap = new Map<string, string>();
  (categories || []).forEach((c) => {
    const key = String(c.id ?? c.name ?? c.nom ?? '');
    const val = String(c.name ?? c.nom ?? '');
    if (key) categoryMap.set(key, val);
  });

  // Gestion responsive des colonnes
  const [cols, setCols] = useState<number>(3);

  useEffect(() => {
    const update = () => setCols(window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 2);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const chunk = <T,>(arr: T[], size: number) => {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  const pages = chunk(products, cols);

  if (products.length === 0) return null;

  return (
    <section className="mb-20">
      {/* Header de section */}
      <div className="flex justify-between items-end mb-6 px-1">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 tracking-tight uppercase">
            {gamme.nom}
          </h2>
          <div className="h-1 w-20 bg-brandRed mt-2 rounded-full"></div>
        </div>
        <Link
          href={gamme.slug ? `/gammes/${gamme.slug}` : "#"}
          className="hidden md:inline-flex items-center text-sm font-semibold text-brandRed hover:text-red-800 transition-colors group"
        >
          Voir toute la collection
          <FaChevronRight className="ml-1 text-xs transition-transform group-hover:translate-x-1" />
        </Link>
      </div>

      <div className="flex flex-col xl:flex-row gap-6">

        {/* Banner Card (Gauche) */}
        <div className="w-full xl:w-1/4 relative group">
          <Link
            href={gamme.slug ? `/gammes/${gamme.slug}` : '#'}
            className="block w-full h-[250px] xl:h-full min-h-[300px] relative rounded-3xl overflow-hidden shadow-lg"
          >
            {gamme.images && gamme.images[0] ? (
              <Image
                src={String(gamme.images[0])}
                alt={gamme.nom}
                fill
                className="object-cover w-full h-full transition-transform duration-700 ease-out group-hover:scale-110"
              />
            ) : (
              <div className="absolute inset-0 bg-gray-200" />
            )}

            {/* Overlay Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-90 transition-opacity group-hover:opacity-100" />

            <div className="absolute left-6 bottom-6 z-20 text-white max-w-[85%]">
              <span className="inline-block px-3 py-1 bg-brandRed/90 backdrop-blur-sm text-[10px] font-bold uppercase tracking-wider rounded-full mb-3 shadow-sm">
                Collection
              </span>
              <h3 className="text-2xl font-bold leading-tight mb-2">{gamme.nom}</h3>
              <p className="text-sm text-gray-200 font-light line-clamp-2">
                Explorez nos meilleures offres pour la gamme {gamme.nom.toLowerCase()}.
              </p>
            </div>
          </Link>
        </div>

        {/* Slider Card (Droite) */}
        <div className="w-full xl:w-3/4 relative bg-gray-50/50 rounded-3xl p-0 md:p-2">

          {/* Custom Navigation Arrows */}
          <div className="hidden md:block pointer-events-none absolute inset-0 z-30 px-2">
            <div className="flex justify-between items-center h-full">
              <button
                onClick={() => swiperRef.current?.slidePrev()}
                className="pointer-events-auto w-10 h-10 rounded-full bg-white/80 backdrop-blur-md shadow-lg flex items-center justify-center text-gray-800 hover:bg-brandRed hover:text-white transition-all transform hover:scale-110 -ml-4"
                aria-label="Précédent"
              >
                <FaChevronLeft className="text-sm" />
              </button>
              <button
                onClick={() => swiperRef.current?.slideNext()}
                className="pointer-events-auto w-10 h-10 rounded-full bg-white/80 backdrop-blur-md shadow-lg flex items-center justify-center text-gray-800 hover:bg-brandRed hover:text-white transition-all transform hover:scale-110 -mr-4"
                aria-label="Suivant"
              >
                <FaChevronRight className="text-sm" />
              </button>
            </div>
          </div>

          <div
            onMouseEnter={() => swiperRef.current?.autoplay?.stop()}
            onMouseLeave={() => swiperRef.current?.autoplay?.start()}
          >
            <Swiper
              modules={[Autoplay]}
              slidesPerView={1}
              loop={pages.length > 1}
              autoplay={{ delay: 5000, disableOnInteraction: false }}
              onSwiper={(s) => (swiperRef.current = s)}
              className="w-full !pb-4 !px-1 pt-1"
              speed={600}
            >
              {pages.map((page, pageIdx) => (
                <SwiperSlide key={pageIdx}>
                  <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                    {page.map((p) => {
                      const toNumber = (v: unknown) => {
                        if (v === null || v === undefined || v === '') return undefined;
                        const n = Number(v);
                        return Number.isFinite(n) ? n : undefined;
                      };

                      const adaptedProduct: ProductCardType = {
                        id: String(p.id),
                        slug: p.slug ?? String(p.id),
                        name: p.name ?? '',
                        sku: p.sku ?? String(p.id),
                        price: toNumber(p.price) ?? 0,
                        promoPrice: toNumber(p.promoPrice) ?? toNumber(p.oldPrice) ?? undefined,
                        stock: toNumber(p.stock) ?? 0,
                        images: p.image ? [p.image] : (p.images ?? []),
                        category: {
                          id: String(p.categoryId ?? p.id),
                          name: categoryMap.get(String(p.categoryId ?? '')) ?? 'Général',
                        },
                      };

                      return (
                        <div key={p.id} className="h-full">
                          <ProductCard product={adaptedProduct} />
                        </div>
                      );
                    })}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Mobile "Voir tout" button (visible only on mobile under slider) */}
          <div className="md:hidden mt-4 text-center">
            <Link
              href={gamme.slug ? `/gammes/${gamme.slug}` : "#"}
              className="inline-block px-6 py-2 bg-white border border-gray-200 rounded-full text-sm font-semibold text-gray-700 shadow-sm"
            >
              Voir tout {gamme.nom}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Page principale ---
export default function Home() {
  const [data, setData] = useState<GammeDetail[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetchGammes = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/gammes/all-with-products-detailed`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json: GammeDetail[] = await res.json();
        if (mounted) setData(json);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        if (mounted) setError(msg || "Erreur de chargement");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchGammes();
    return () => { mounted = false; };
  }, []);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] text-red-600 font-medium">
        <div className="bg-red-50 px-6 py-4 rounded-xl border border-red-100">
          Erreur : {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <SaleBanner />
      <Header />
      <main className="bg-white min-h-screen font-sans text-gray-800 pb-20">
        <div className="container max-w-7xl mx-auto px-4 md:px-6 py-12">

          {loading ? (
            <SkeletonLoader />
          ) : !data || data.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Aucune gamme trouvée.</div>
          ) : (
            <>
              {/* --- Section Tout Gammes (Carousel Horizontal) --- */}
              <div className="mb-16">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                    Nos Univers
                  </h2>
                </div>

                <div className="
                  flex gap-4 overflow-x-auto pb-6 -mx-4 px-4 
                  snap-x snap-mandatory 
                  md:grid md:grid-cols-4 lg:grid-cols-6 md:gap-6 md:overflow-visible md:mx-0 md:px-0
                  scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent
                ">
                  {data.map((d) => (
                    <Link
                      key={d.gamme.id}
                      href={d.gamme.slug ? `/gammes/${d.gamme.slug}` : "#"}
                      className="group min-w-[150px] w-[150px] md:w-auto flex-shrink-0 snap-center relative"
                    >
                      <div className="aspect-square rounded-2xl overflow-hidden relative shadow-md group-hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
                        {d.gamme.images && d.gamme.images[0] ? (
                          <>
                            <Image
                              src={String(d.gamme.images[0])}
                              alt={d.gamme.nom}
                              fill
                              sizes="(max-width: 768px) 150px, 200px"
                              className="object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-300" />
                          </>
                        ) : (
                          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                            <span className="text-gray-300 font-bold text-4xl">?</span>
                          </div>
                        )}

                        <div className="absolute inset-0 flex items-center justify-center p-2">
                          <span className="text-white font-bold uppercase text-center text-sm md:text-base drop-shadow-md border-b-2 border-transparent group-hover:border-white transition-all pb-0.5">
                            {d.gamme.nom}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* --- Sections par Gamme --- */}
              <div className="space-y-16">
                {data.map((d) => <ProductSection key={d.gamme.id} detail={d as GammeDetail} />)}
              </div>
            </>
          )}
        </div>
      </main>
    </>
  );
}